// imports
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Collapse, Typography } from '@mui/material';

// local imports
import { mapActions, RootState } from '../../../../store';
import StyledSwitch from '../../StyledSwitch';
import AdditionalLayerBorderControls from './AdditionalLayerBorderControls/AdditionalLayerBorderControls';
import LayerLegend from './LayerLegend/LayerLegend';
import LayerSource from './LayerSource/LayerSource';
import OpacityControl from '../../OpacityControl/OpacityControl';
import PointLayerControls from './PointLayerControls/PointLayerControls';
import {
  isGeoJsonAdditionalLayer,
  isMVTAdditionalLayer,
} from '../../../../configs/additionalLayers';
import LineLayerControls from './LineLayerControls/LineLayerControls';
import { getAriaLabel } from '../../../../utils/ariaLabels';
import { useZoomDisabled } from '../../../../hooks/useZoomDisabled';
import ZoomDisabledBanner from '../ZoomDisabledBanner';

interface AdditionalLayerProps {
  id: string;
}

/**
 * AdditionalLayer
 * Refactored to use Box elements for better accessibility compliance
 *
 * Renders a container with layer name and toggle switch.
 * When layer is active, expanded view contains an opacity slider and border color/width inputs.
 *
 * @param {string} id - The id of the layer.
 * @returns {JSX.Element} The rendered additional layer component.
 */
export default function AdditionalLayer({ id }: AdditionalLayerProps) {
  const dispatch = useDispatch();
  const layer = useSelector(
    (state: RootState) =>
      state.map?.layers?.[id as keyof RootState['map']['layers']]
  );
  const isZoomDisabled = useZoomDisabled(id);

  // Toggles the layer active state in Redux
  const handleToggle = () => {
    dispatch(mapActions.setActive({ id, active: !layer?.active }));
  };

  // Handle opacity changes from the OpacityControl component
  const handleOpacityChange = useCallback(
    (opacity: number) => {
      dispatch(mapActions.setOpacity({ id, opacity }));
    },
    [dispatch, id]
  );

  return (
    <Box>
      {/* Layer Header */}
      <Box
        key={id}
        aria-labelledby={`layer-toggle-label-${id}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          pt: 2,
          pb: 1,
        }}
      >
        <Typography
          component="span"
          id={`layer-toggle-label-${id}`}
          data-testid="additional-layer-name"
          variant="body1"
          sx={{
            fontWeight: 'bold',
            flexGrow: 1,
            textAlign: 'left',
            display: 'block',
            overflowWrap: 'break-word',
            fontSize: '1.125rem',
          }}
        >
          {layer?.name}
        </Typography>
        <StyledSwitch
          edge="end"
          onChange={handleToggle}
          checked={layer?.active}
          slotProps={{
            input: {
              id: `layer-toggle-${id}`,
              'aria-label': getAriaLabel(layer?.name || ''),
              role: 'switch',
            },
          }}
        />
      </Box>

      {/* Zoom-disabled banner */}
      {layer?.active && isZoomDisabled && layer?.minZoom && (
        <ZoomDisabledBanner minZoom={layer.minZoom} />
      )}

      {/* Collapsible Controls */}
      <Collapse in={layer?.active && !isZoomDisabled} timeout="auto" unmountOnExit>
        <Box sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pl: 0,
              pr: 0,
            }}
          >
            {/* Opacity Controls */}
            <OpacityControl
              value={layer?.opacity ?? 100}
              onChange={handleOpacityChange}
              data-testid="opacity-slider"
            />
            {/* Conditional Layer Controls */}
            {(isMVTAdditionalLayer(layer) || isGeoJsonAdditionalLayer(layer)) &&
              layer.geomType === 'polygon' && (
                <AdditionalLayerBorderControls layer={layer} />
              )}

            {/* Show legend for layers with a colormap */}
            {layer && 'colormap' in layer && layer.colormap && (
              <LayerLegend layer={layer} />
            )}
            {isMVTAdditionalLayer(layer) && layer.geomType === 'point' && (
              <PointLayerControls layer={layer} />
            )}
            {isGeoJsonAdditionalLayer(layer) && layer.geomType === 'line' && (
              <LineLayerControls layer={layer} />
            )}
            {isGeoJsonAdditionalLayer(layer) && layer.geomType === 'point' && (
              <PointLayerControls layer={layer} />
            )}

          </Box>
        </Box>
      </Collapse>

      {/* Source URL - visible whenever layer is active */}
      {layer?.active && layer && <LayerSource layer={layer} />}
    </Box>
  );
}
